// ** React Imports
import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

// ** Next Imports

// ** MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import Tooltip from '@mui/material/Tooltip'
import { styled, createTheme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import MuiTabList from '@mui/lab/TabList'
import LinearProgress from '@mui/material/LinearProgress'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Styled Components
import CustomHeader from 'src/@core/components/Header'
import {
  Alert,
  AlertTitle,
  AppBar,
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Snackbar,
  Toolbar,
  useMediaQuery
} from '@mui/material'
import { AccountTree, AddSharp, AddToPhotos, RemoveSharp } from '@mui/icons-material'

import { highCollegeOptions } from 'src/data/college'
import HuecoBarraInferior from 'src/@core/components/layout/HuecoBarraInferior'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'
import { construirEnlace, leerEstadoDeUrl } from 'src/@core/utils/enlace-compartible'

/*
  Tabla de referencia renderizada en el servidor.

  Por que existe: /college/ servia 856 palabras y cero filas sobre 97 KB de datos.
  Las 31 investigaciones y sus 260 niveles viven dentro de tarjetas que solo se
  pintan al interactuar, asi que ninguna llegaba al HTML.

  No duplica /building/: alli las filas de estas investigaciones estan por nivel de
  edificio y con su coste en recursos; aqui estan por nivel de investigacion, con el
  azurite de cada uno y —esto es lo que no esta publicado en ningun otro sitio— el
  valor de stat que da cada nivel.

  El texto del efecto lleva un marcador $1 que la herramienta sustituye por el valor
  del nivel. Aqui se sustituye igual, para que cada fila diga lo que hace ese nivel
  y no una plantilla con un simbolo.
*/
const COLUMNAS_COLLEGE = [
  { clave: 'investigacion', encabezado: 'Research' },
  { clave: 'nivel', encabezado: 'Level', numerica: true },
  { clave: 'azurite', encabezado: 'Azurite', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'acumulado', encabezado: 'Cumulative', numerica: true, formato: v => v.toLocaleString('en-US') },
  { clave: 'efecto', encabezado: 'Effect at this level' }
]

const FILAS_COLLEGE = highCollegeOptions.flatMap(o => {
  let acumulado = 0

  return (o.levels ?? []).map(l => {
    acumulado += l.points ?? 0
    const valor = Array.isArray(l.stat) ? l.stat.join(' / ') : l.stat

    return {
      id: o.row + o.col + '-' + l.level,
      investigacion: o.description,
      nivel: l.level,
      azurite: l.points ?? 0,
      acumulado,
      efecto: String(o.statText ?? '').replace(/\$1/g, valor)
    }
  })
})


// ** Styled component for the link in the dataTable
const theme = createTheme()

const GreenButton = styled(Button)(({ theme }) => ({
  // Verde oscuro y texto blanco: #E8F1F5 sobre #1B8A6B daba 3,74:1, por debajo de AA.
  backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
    filter: 'brightness(0.85)'
  }
}))

const RedButton = styled(Button)(({ theme }) => ({
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
    filter: 'brightness(0.85)'
  }
}))

const TabList = styled(MuiTabList)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    display: 'none'
  },
  '& .Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    // contrastText: en modo oscuro el primary es claro y el blanco daba 2,30:1
    color: `${theme.palette.primary.contrastText} !important`
  },
  '& .MuiTab-root': {
    minWidth: 65,
    minHeight: 38,
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.up('sm')]: {
      minWidth: 130
    }
  }
}))

// ** Var custom
const defaultCollegeInfo = {
  options: [...highCollegeOptions],
  userTalents: 0
}

/*
  Puntos acumulados hasta un nivel. La misma cuenta que hace handleFillAll sumando
  los `points` de cada nivel, cortada en el nivel pedido.
*/
const nivelATotal = (opcion, nivel) => (opcion.levels ?? []).slice(0, nivel).reduce((acc, l) => acc + l.points, 0)

/*
  Lo que viaja en un enlace compartido: el azurite disponible y el nivel elegido en
  cada una de las 31 investigaciones. El resto se reconstruye desde
  highCollegeOptions, que ya esta en el bundle.

  Cada investigacion se referencia por fila+columna y no por su posicion en el
  array: si mañana entra una nueva en medio, los enlaces viejos siguen apuntando a
  las mismas.
*/
const aEstadoMinimo = info => ({
  t: info.userTalents || 0,
  o: info.options.reduce((acc, o) => {
    if (o.counter > 0) acc[`${o.row}${o.col}`] = o.counter

    return acc
  }, {})
})

const desdeEstadoMinimo = minimo => {
  if (!minimo || typeof minimo !== 'object' || !minimo.o || typeof minimo.o !== 'object') return null

  return {
    userTalents: Number(minimo.t) || 0,
    options: [...highCollegeOptions].map(o => {
      // Un enlace tocado a mano no puede dejar la herramienta en un estado imposible:
      // el nivel se recorta a lo que esa investigacion admite.
      const pedido = Number(minimo.o[`${o.row}${o.col}`]) || 0
      const counter = Math.min(Math.max(pedido, o.minCounter ?? 0), o.maxCounter ?? 0)

      return { ...o, counter, curLevel: counter, curTotalPoints: nivelATotal(o, counter) }
    })
  }
}

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress color='success' variant='determinate' aria-label='Progress' {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant='body2' sx={{ color: 'common.white' }}>{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  )
}

LinearProgressWithLabel.propTypes = {
  /**
   * The value of the progress indicator for the determinate and buffer variants.
   * Value between 0 and 100.
   */
  value: PropTypes.number.isRequired
}

 
const HighCollege = () => {
  // ** State
  const activeTab = 'talents'
  const [isLoading, setIsLoading] = useState(true)
  const hideText = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const [collegeInfo, setCollegeInfo] = useState(defaultCollegeInfo)


  const [snackOpen, setSnackOpen] = useState({ open: false })

  const [totalAvailablePoints, setTotalAvailablePoints] = useState(
    [...highCollegeOptions]
      .filter(o => o.beast === 'all' || o.beast === 'any')
      .map(o => o.levels)
      .map(od => {
        return od.reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
      })
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
  )

  const totalPoints = collegeInfo.options.reduce(
    (accumulator, currentValue) => accumulator + currentValue.curTotalPoints,
    0
  )

  // ** Hooks
  useEffect(() => {
    /*
      El enlace compartido gana sobre lo guardado: quien abre un enlace de su alianza
      quiere ver esa configuracion, no la suya. Lo propio sigue en localStorage y
      vuelve al entrar por /college/ sin parametros; para quedarse con lo que llego
      por el enlace hay que pulsar SAVE.
    */
    const compartido = desdeEstadoMinimo(leerEstadoDeUrl())
    if (compartido) {
      setCollegeInfo(compartido)
      setIsLoading(false)

      return
    }

    let saved
    if (typeof window !== 'undefined') {
      // Perform localStorage action
      saved = JSON.parse(localStorage.getItem('collegeInfo'))
    }
    if (saved) {
      setCollegeInfo(saved)
    } else {
      const newOptions = [...highCollegeOptions].filter(o => o.beast === 'all' || o.beast === 'any')
      setCollegeInfo({ ...defaultCollegeInfo, options: newOptions })
    }
    setIsLoading(false)
  }, [])

  /*
    `progress` es un valor derivado, no estado.

    Estaba en useState y se escribia desde un useEffect, asi que cada clic en
    + o - provocaba dos renders completos del arbol en vez de uno: el del
    cambio de opciones y, acto seguido, el de setProgress. Con 19 tarjetas en
    beast y 31 en college, eso es el doble de trabajo de React por cada
    interaccion, y es lo que mide INP.

    useMemo lo calcula durante el render que ya estaba ocurriendo. Efecto
    lateral: la barra ya nace con su valor real en vez de saltar desde 0.
  */
  const progress = useMemo(() => {
    if (collegeInfo.userTalents > 0) {
      return 100 - ((collegeInfo.userTalents - totalPoints) / collegeInfo.userTalents) * 100
    }

    return 100 - ((totalAvailablePoints - totalPoints) / totalAvailablePoints) * 100
  }, [totalPoints, totalAvailablePoints, collegeInfo.userTalents])

  const handleSaveData = e => {
    localStorage.setItem('collegeInfo', JSON.stringify(collegeInfo))

    return setSnackOpen({ open: `Information Stored in the browser!`, type: 'success', time: 3000 })
  }

  const snackClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackOpen({ open: false })
  }

  const handlePrerequisiteFill = (row, col) => {
    let newOptionArray = [...collegeInfo.options]
    let expArrayFiltered
    let dependencyArray = []

    const optionObj = collegeInfo.options?.find(o => o.row === row && o.col === col)
    const reqArray = optionObj.dependency
    const reqArrayB = optionObj?.dependencyB

    if (reqArray?.length > 0) {
      reqArray.forEach(d => {
        const checkDependency = collegeInfo.options?.find(opt => opt.row === d.row && opt.col === d.col)
        if (d?.minLevel > checkDependency?.curLevel) {
          // Verify dependency
          dependencyArray.push({
            description: checkDependency?.description,
            minLevel: d?.minLevel,
            counter: d?.minLevel
          })

          if (d?.minLevel > 0) {
            expArrayFiltered = checkDependency.levels?.filter(obj => obj.level > 0 && obj.level <= d?.minLevel)
          }

          const newPointsNeeded = expArrayFiltered?.reduce(
            (accumulator, currentValue) => accumulator + currentValue.points,
            0
          )

          newOptionArray = newOptionArray.map(obj =>
            obj.row === checkDependency.row && obj.col === checkDependency.col
              ? { ...checkDependency, counter: d?.minLevel, curLevel: d?.minLevel, curTotalPoints: newPointsNeeded }
              : obj
          )
        }
      })
    }

    if (reqArrayB?.length > 0 && optionObj?.curLevel >= optionObj?.dependencyBlevel) {
      reqArrayB.forEach(d => {
        const checkDependency = collegeInfo.options?.find(opt => opt.row === d.row && opt.col === d.col)
        if (d?.minLevel > checkDependency?.curLevel) {
          // Verify dependency
          dependencyArray.push({
            description: checkDependency?.description,
            minLevel: d?.minLevel,
            counter: d?.minLevel
          })

          if (d?.minLevel > 0) {
            expArrayFiltered = checkDependency.levels?.filter(obj => obj.level > 0 && obj.level <= d?.minLevel)
          }

          const newPointsNeeded = expArrayFiltered?.reduce(
            (accumulator, currentValue) => accumulator + currentValue.points,
            0
          )

          newOptionArray = newOptionArray.map(obj =>
            obj.row === checkDependency.row && obj.col === checkDependency.col
              ? { ...checkDependency, counter: d?.minLevel, curLevel: d?.minLevel, curTotalPoints: newPointsNeeded }
              : obj
          )
        }
      })
    }

    const checkPointsLimit = [...newOptionArray]
      .filter(o => o.beast === 'all' || o.beast === 'any')
      .reduce((accumulator, currentValue) => accumulator + currentValue.curTotalPoints, 0)

    if (checkPointsLimit > collegeInfo.userTalents && collegeInfo.userTalents > 0)
      return setSnackOpen({
        open: `You dont have enough AZURITE available. You need ${checkPointsLimit?.toLocaleString()} and you have a limit of ${collegeInfo.userTalents?.toLocaleString()} `,
        type: 'warning',
        time: 3000
      })

    setCollegeInfo(prevState => ({ ...prevState, options: newOptionArray }))

    setSnackOpen({ open: `Prerequisites loaded.`, type: 'success', time: 3000 })
  }

  const handleIncrease = (row, col, incAll = false) => {
    const oldObject = collegeInfo.options.find(r => r.row === row && r.col === col)
    if (oldObject?.counter === oldObject?.maxCounter) return

    let newTotalPoints = 0
    let newState

    const newCounter = oldObject.counter === oldObject.maxCounter ? oldObject.maxCounter : oldObject.counter + 1
    const dependencyObj = []

    let dependencyMsj = '',
      dependencyMet = false

    if (oldObject?.dependency?.length > 0) {
      oldObject.dependency.forEach(d => {
        const checkDependency = collegeInfo.options.find(o => o.row === d.row && o.col === d.col)
        if (d?.minLevel > checkDependency?.counter) {
          dependencyObj.push({
            row: checkDependency?.row,
            col: checkDependency?.col,
            counter: checkDependency?.counter,
            description: checkDependency?.description,
            minLevel: d?.minLevel
          })
        }
      })

      if (dependencyObj?.length > 0) {
        dependencyMet = false
        dependencyMsj = dependencyObj
          ?.map(d => {
            return `Needs ${d?.description} to reach lv${d?.minLevel}. (${d?.counter}/${d?.minLevel}). `
          })
          .join('')
      } else {
        dependencyMet = true
      }
    }

    if (oldObject?.dependencyB?.length > 0 && newCounter > oldObject?.dependencyBlevel) {
      oldObject.dependencyB.forEach(d => {
        const checkDependency = collegeInfo.options.find(o => o.row === d.row && o.col === d.col)
        if (d?.minLevel > checkDependency?.counter) {
          dependencyObj.push({
            row: checkDependency?.row,
            col: checkDependency?.col,
            counter: checkDependency?.counter,
            description: checkDependency?.description,
            minLevel: d?.minLevel
          })
        }
      })

      if (dependencyObj?.length > 0) {
        dependencyMet = false
        dependencyMsj = dependencyObj
          ?.map(d => {
            return `Needs ${d?.description} to reach lv${d?.minLevel}. (${d?.counter}/${d?.minLevel}).`
          })
          .join('')
      } else {
        dependencyMet = true
      }
    }

    if (oldObject.dependency === 0 && typeof oldObject.dependency === 'number') dependencyMet = true

    if (!dependencyMet) {
      const message = dependencyMsj
      dependencyMsj = ''

      // `The conditions to upgrade the Talent aren't met: `
      return setSnackOpen({ open: message, type: 'warning', time: 6000 })
    }

    const oldTotalPoints = oldObject?.curTotalPoints

    // Fill skill at maxCounter
    if (incAll) {
      if (row === 'I' && col === 1 && oldObject?.dependencyBlevel > oldObject?.counter) {
        newTotalPoints = oldObject?.levels
          ?.filter(l => l.level > oldObject.counter && l.level < oldObject?.dependencyBlevel)
          .reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
        newState = collegeInfo.options?.map(obj =>
          obj.row === row && obj.col === col
            ? {
                ...obj,
                counter: oldObject?.dependencyBlevel,
                curLevel: oldObject?.dependencyBlevel,
                curTotalPoints: oldTotalPoints + newTotalPoints
              }
            : obj
        )
      } else {
        newTotalPoints = oldObject?.levels
          ?.filter(l => l.level > oldObject.counter)
          .reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
        newState = collegeInfo.options?.map(obj =>
          obj.row === row && obj.col === col
            ? {
                ...obj,
                counter: oldObject.maxCounter,
                curLevel: oldObject.maxCounter,
                curTotalPoints: oldTotalPoints + newTotalPoints
              }
            : obj
        )
      }
    } else {
      newTotalPoints = oldObject?.levels?.find(l => l.level === newCounter)?.points
      newState = collegeInfo.options?.map(obj =>
        obj.row === row && obj.col === col
          ? { ...obj, counter: newCounter, curLevel: newCounter, curTotalPoints: oldTotalPoints + newTotalPoints }
          : obj
      )
    }

    const checkPointsLimit = [...newState]
      .filter(o => o.beast === 'all' || o.beast === 'any')
      .reduce((accumulator, currentValue) => accumulator + currentValue.curTotalPoints, 0)

    if (checkPointsLimit > collegeInfo.userTalents && collegeInfo.userTalents > 0)
      return setSnackOpen({
        open: `You dont have enough AZURITE available. You need ${checkPointsLimit?.toLocaleString()} and you have a limit of ${collegeInfo.userTalents?.toLocaleString()} `,
        type: 'warning',
        time: 3000
      })

    setCollegeInfo(prevState => ({ ...prevState, options: newState }))
  }

  const handleDrecrease = (row, col) => {
    const oldObject = collegeInfo.options.find(r => r.row === row && r.col === col)
    if (oldObject?.counter === oldObject?.minCounter) return
    const newCounter = oldObject.counter

    const oldTotalPoints = oldObject?.curTotalPoints
    const newTotalPoints = oldObject?.levels?.find(l => l.level === newCounter)?.points

    const newState = collegeInfo.options?.map(obj =>
      obj.row === row && obj.col === col
        ? { ...obj, counter: newCounter - 1, curLevel: newCounter - 1, curTotalPoints: oldTotalPoints - newTotalPoints }
        : obj
    )
    setCollegeInfo(prevState => ({ ...prevState, options: newState }))
  }

  const handleFillAll = e => {
    const newState = [...collegeInfo.options]?.map(obj => {
      return {
        ...obj,
        counter: obj.maxCounter,
        curLevel: obj.maxCounter,
        curTotalPoints: obj.levels.reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
      }
    })

    const totalPoints = newState
      .filter(o => o.beast === 'all' || o.beast === 'any')
      .map(o => o.levels)
      .map(od => {
        return od.reduce((accumulator, currentValue) => accumulator + currentValue.points, 0)
      })
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0)

    const checkPointsLimit = totalPoints

    if (checkPointsLimit > collegeInfo.userTalents && collegeInfo.userTalents > 0)
      return setSnackOpen({
        open: `You dont have enough AZURITE available. You need ${checkPointsLimit?.toLocaleString()} and you have a limit of ${collegeInfo.userTalents?.toLocaleString()} `,
        type: 'warning',
        time: 3000
      })

    setTotalAvailablePoints(totalPoints)
    setCollegeInfo(prevState => ({ ...prevState, options: newState }))
  }

  const handleClearAll = e => {
    setCollegeInfo(prevState => ({
      ...prevState,
      options: [...highCollegeOptions].filter(o => o.beast === 'all' || o.beast === 'any')
    }))
  }

  const handleShare = async () => {
    const enlace = construirEnlace(aEstadoMinimo(collegeInfo))
    if (!enlace) return setSnackOpen({ open: 'Could not build the link.', type: 'error', time: 4000 })

    try {
      await navigator.clipboard.writeText(enlace)

      return setSnackOpen({ open: 'Link copied — paste it anywhere.', type: 'success', time: 4000 })
    } catch (e) {
      /*
        El portapapeles necesita https y, en algunos navegadores, un gesto reciente.
        Si lo rechaza, la URL va a la barra de direcciones: menos comodo, pero el
        enlace existe. Peor seria decir "copiado" sin haber copiado nada.
      */
      window.history.replaceState(null, '', enlace)

      return setSnackOpen({ open: 'Link is now in the address bar — copy it from there.', type: 'info', time: 6000 })
    }
  }

  const handleRemoveItems = e => {
    setCollegeInfo({ ...defaultCollegeInfo })
    localStorage.removeItem('collegeInfo')

    return setSnackOpen({ open: `Information Cleared from the browser.`, type: 'error' })
  }

  const tabContentList = {
    talents: (
      <>
        <Card>
          <Box sx={{ flexGrow: 1 }}>
            <Snackbar
              open={snackOpen.open ? true : false}
              autoHideDuration={snackOpen.time ?? 7000}
              onClose={snackClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert variant='filled' onClose={snackClose} severity={snackOpen.type}>
                <AlertTitle>{snackOpen.type === 'success' ? 'Done!' : 'Info:'}</AlertTitle>
                {snackOpen.open}
              </Alert>
            </Snackbar>
            <Grid container spacing={2} sx={{ padding: '0.5em' }}>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex' }}>
                <Paper elevation={1} sx={{ width: '100%' }}>
                  <Divider light>{<Typography variant='caption'>USER CONFIGURATION</Typography>}</Divider>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      marginTop: '10px',
                      padding: '0.5em',
                      gap: '1rem',
                      alignItems: 'center'
                    }}
                  >
                    <GreenButton
                      variant='contained'
                      startIcon={<Icon icon='ic:baseline-save' />}
                      onClick={handleSaveData}
                    >
                      SAVE
                    </GreenButton>
                    <RedButton
                      edge='start'
                      variant='contained'
                      startIcon={<Icon icon='ic:baseline-delete' />}
                      onClick={handleRemoveItems}
                    >
                      DELETE
                    </RedButton>
                    <Button variant='outlined' startIcon={<Icon icon='ic:baseline-link' />} onClick={handleShare}>
                      SHARE
                    </Button>
                  </Box>
                  <Divider />
                  <Box
                    elevation={4}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '0.6em'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: '0.5em',
                          flexWrap: 'wrap'
                        }}
                      >
                        <Box>
                          <Alert severity='warning' sx={{ m: 1 }}>
                            There are some skills missing azurite requeriments, like Manage Food II lv3+ and Rally
                            Decree I lv3+. If you want to help with this missing info, check CONTRIBUTORS tab.{' '}
                          </Alert>
                          <Alert severity='info' sx={{ m: 1 }}>
                            Requirement azurite for College levels are not being counted, just the skills. Price is an
                            average of 490 azurite / $8.{' '}
                          </Alert>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            padding: '0.5em',
                            flexWrap: 'wrap'
                          }}
                        >
                          <Typography variant='body2' color='primary' align='right' sx={{ marginRight: '3px' }}>
                            Set AZURITE Limit:
                          </Typography>

                          <TextField
                            slotProps={{ htmlInput: { 'aria-label': 'Set AZURITE Limit' } }}
                            id='talents-limit'
                            size='small'
                            variant='outlined'
                            type='number'
                            sx={{ maxWidth: '150px' }}
                            value={collegeInfo.userTalents > 0 ? collegeInfo.userTalents : ''}
                            onChange={e => {
                              const value =
                                +e.target.value >= totalAvailablePoints ? totalAvailablePoints : +e.target.value

                              setCollegeInfo(prevState => ({
                                ...prevState,
                                userTalents: value
                              }))
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Divider light>{<Typography variant='caption'>TREE</Typography>}</Divider>
                  <Box sx={{ minWidth: `${hideText ? '1px' : '520px'}` }}>
                  {/*
                    `opt` ya es el elemento: este map recorre las propias
                    opciones, asi que buscarlo otra vez por row/col devolvia
                    siempre el mismo objeto. Eran 21 recorridos lineales del
                    array por tarjeta, en cada render, y React vuelve a
                    renderizar todas las tarjetas en cada clic de + o -.
                    Medido a 4x de CPU: 223 ms de script por clic.
                  */}
                    {collegeInfo.options.map(opt => {
                      if (
                        opt.type === 1 &&
                        (opt.beast === collegeInfo.beast || opt.beast === 'all' || opt.beast === 'any')
                      ) {
                        return (
                          <>
                            <Grid container>
                              <Grid size={{ xs: 3, sm: 3 }}key={`${opt.row}-col1_empty`}></Grid>
                              <Grid size={{ xs: 6, sm: 6 }}key={`${opt.row}-col1`} sx={{ padding: '0.5rem' }}>
                                <Paper
                                  elevation={12}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    width: `${hideText ? '150px' : '200px'}`
                                  }}
                                  key={`${opt.row}-col1-paper`}
                                >
                                  <Box
                                    sx={{
                                      margin: theme.spacing(1),
                                      minWidth: 90
                                    }}
                                    key={`${opt.row}-col1-paper-box`}
                                  >
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginBottom: '10px' }}
                                    >
                                      {collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.description}
                                    </Typography>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 'revert',
                                        alignContent: 'center',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                      }}
                                      key={`${opt.row}-col1-paper-box-sub`}
                                    >
                                      <Badge
                                        color={
                                          opt
                                            ?.counter ===
                                          opt
                                            ?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        badgeContent={
                                          opt
                                            ?.curTotalPoints
                                        }
                                        max={50001}
                                      >
                                        <Avatar
                                          alt={`${opt.row}_${opt.col}`}
                                          src={`/images/college/row${opt.row}_${opt.col}.webp`}
                                          sx={{
                                            width: theme.spacing(7),
                                            height: theme.spacing(7)
                                          }}
                                        />
                                      </Badge>
                                      <Typography
                                        color={
                                          opt
                                            ?.counter ===
                                          opt
                                            ?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        variant='body2'
                                        sx={{
                                          marginTop: '-12px'
                                        }}
                                      >
                                        {
                                          opt
                                            ?.counter
                                        }{' '}
                                        /{' '}
                                        {
                                          opt
                                            ?.maxCounter
                                        }{' '}
                                      </Typography>

                                      {/* MAXED */}
                                      {opt
                                        ?.counter ===
                                        opt
                                          ?.maxCounter && (
                                        <Typography
                                          color={
                                            opt
                                              ?.counter ===
                                            opt
                                              ?.maxCounter
                                              ? 'error'
                                              : 'primary'
                                          }
                                          variant='body2'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          {opt
                                            ?.counter ===
                                          opt
                                            ?.maxCounter
                                            ? '(MAXED)'
                                            : ''}
                                        </Typography>
                                      )}

                                      {/* NEXT LEVEL */}
                                      {opt
                                        ?.counter !==
                                        opt
                                          ?.maxCounter && (
                                        <Typography
                                          color='textPrimary'
                                          variant='caption'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          NEXT LVL: +{opt.levels?.find(l => l.level === opt.curLevel + 1)?.points}
                                        </Typography>
                                      )}

                                      <ButtonGroup
                                        color='primary'
                                        size='small'
                                        sx={{
                                          marginTop: '10px'
                                        }}
                                      >
                                        <Button
                                          aria-label='Decrease one level'
                                          disabled={
                                            opt
                                              ?.counter ===
                                            opt
                                              ?.minCounter
                                              ? true
                                              : false
                                          }
                                          onClick={() => {
                                            handleDrecrease(opt.row, opt.col)
                                          }}
                                        >
                                          <RemoveSharp fontSize='small' />
                                        </Button>
                                        <Button
                                          aria-label='Increase one level'
                                          onClick={() => {
                                            handleIncrease(opt.row, opt.col)
                                          }}
                                          disabled={
                                            opt
                                              ?.counter ===
                                            opt
                                              ?.maxCounter
                                              ? true
                                              : false
                                          }
                                        >
                                          <AddSharp fontSize='small' />
                                        </Button>
                                        <Tooltip describeChild title={'Fill skill at max.'}>
                                          <Button
                                            aria-label='increase-all'
                                            onClick={() => {
                                              handleIncrease(opt.row, opt.col, true)
                                            }}
                                            disabled={
                                              opt
                                                ?.counter ===
                                              opt
                                                ?.maxCounter
                                                ? true
                                                : false
                                            }
                                          >
                                            <AddToPhotos />{' '}
                                          </Button>
                                        </Tooltip>
                                        {opt.row !== 'A' && !hideText && (
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 1)
                                              }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        )}
                                      </ButtonGroup>
                                      {opt.row !== 'A' && hideText && (
                                        <ButtonGroup
                                          color='primary'
                                          size='small'
                                          sx={{
                                            marginTop: '5px',
                                            minHeight: '32px',
                                            minWidth: '48px'
                                          }}
                                        >
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 1)
                                              }}
                                              sx={{ minHeight: '32px', minWidth: '48px' }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        </ButtonGroup>
                                      )}
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>

                              <Grid size={{ xs: 3, sm: 3 }}key={`${opt.row}-col2_empty`}></Grid>
                            </Grid>

                            <Divider light />
                          </>
                        )
                      } else if (
                        opt.type === 2 &&
                        opt.col === 1 &&
                        (opt.beast === collegeInfo.beast || opt.beast === 'all' || opt.beast === 'any')
                      ) {
                        return (
                          <>
                            <Grid container>
                              {/* LEFT */}
                              <Grid size={{ xs: 6, sm: 6, md: 5, lg: 5 }}key={`${opt.row}-col1`} sx={{ padding: '0.5rem' }}>
                                <Paper
                                  elevation={12}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    width: `${hideText ? '150px' : '200px'}`
                                  }}
                                  key={`${opt.row}-col1-paper`}
                                >
                                  <Box
                                    sx={{
                                      margin: theme.spacing(1),
                                      minWidth: 90
                                    }}
                                    key={`${opt.row}-col1-paper-box`}
                                  >
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginBottom: '10px' }}
                                    >
                                      {collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.description}
                                    </Typography>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 'revert',
                                        alignContent: 'center',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                      }}
                                      key={`${opt.row}-col1-paper-box-sub`}
                                    >
                                      <Badge
                                        color={
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        badgeContent={
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)
                                            ?.curTotalPoints
                                        }
                                        max={50001}
                                      >
                                        <Avatar
                                          alt={`${opt.row}_${opt.col}`}
                                          src={`/images/college/row${opt.row}_1.webp`}
                                          sx={{
                                            width: theme.spacing(7),
                                            height: theme.spacing(7)
                                          }}
                                        />
                                      </Badge>

                                      <Typography
                                        color={
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        variant='body2'
                                        sx={{
                                          marginTop: '-12px'
                                        }}
                                      >
                                        {collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter} /{' '}
                                        {collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter}{' '}
                                      </Typography>

                                      {/* MAXED */}
                                      {collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                        collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)
                                          ?.maxCounter && (
                                        <Typography
                                          color={
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)
                                              ?.counter ===
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                              ? 'error'
                                              : 'primary'
                                          }
                                          variant='body2'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          {collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter ===
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                            ? '(MAXED)'
                                            : ''}
                                        </Typography>
                                      )}

                                      {/* NEXT LEVEL */}
                                      {collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.counter !==
                                        collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)
                                          ?.maxCounter && (
                                        <Typography
                                          color='textPrimary'
                                          variant='caption'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          NEXT LVL: +{opt.levels?.find(l => l.level === opt.counter + 1)?.points}
                                        </Typography>
                                      )}
                                      <ButtonGroup
                                        color='primary'
                                        size='small'
                                        sx={{
                                          marginTop: '10px'
                                        }}
                                      >
                                        <Button
                                          aria-label='Decrease one level'
                                          disabled={
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)
                                              ?.counter ===
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.minCounter
                                              ? true
                                              : false
                                          }
                                          onClick={() => {
                                            handleDrecrease(opt.row, 1)
                                          }}
                                        >
                                          <RemoveSharp fontSize='small' />
                                        </Button>
                                        <Button
                                          aria-label='Increase one level'
                                          onClick={() => {
                                            handleIncrease(opt.row, 1)
                                          }}
                                          disabled={
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)
                                              ?.counter ===
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)?.maxCounter
                                              ? true
                                              : false
                                          }
                                        >
                                          <AddSharp fontSize='small' />
                                        </Button>
                                        <Tooltip describeChild title={'Fill skill at max.'}>
                                          <Button
                                            aria-label='increase-all'
                                            onClick={() => {
                                              handleIncrease(opt.row, 1, true)
                                            }}
                                            disabled={
                                              collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)
                                                ?.counter ===
                                              collegeInfo.options?.find(o => o.row === opt.row && o.col === 1)
                                                ?.maxCounter
                                                ? true
                                                : false
                                            }
                                          >
                                            <AddToPhotos />{' '}
                                          </Button>
                                        </Tooltip>
                                        {!hideText && (
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 1)
                                              }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        )}
                                      </ButtonGroup>
                                      {hideText && (
                                        <ButtonGroup
                                          color='primary'
                                          size='small'
                                          sx={{
                                            marginTop: '5px',
                                            minHeight: '32px',
                                            minWidth: '48px'
                                          }}
                                        >
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 1)
                                              }}
                                              sx={{ minHeight: '32px', minWidth: '48px' }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        </ButtonGroup>
                                      )}
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                              {/* MIDDLE */}
                              <Grid size={{ md: 2, lg: 2 }}key={`${opt.row}-col2_empty`}></Grid>
                              {/* RIGHT */}
                              <Grid size={{ xs: 6, sm: 6, md: 5, lg: 5 }}key={`${opt.row}-col2`} sx={{ padding: '0.5rem' }}>
                                <Paper
                                  elevation={12}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    width: `${hideText ? '150px' : '200px'}`
                                  }}
                                  key={`${opt.row}-col2-paper`}
                                >
                                  <Box
                                    sx={{
                                      margin: theme.spacing(1),
                                      minWidth: 90
                                    }}
                                    key={`${opt.row}-col2-paper-box`}
                                  >
                                    <Typography
                                      variant='body2'
                                      color='primary'
                                      align='center'
                                      sx={{ marginBottom: '10px' }}
                                    >
                                      {collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.description}
                                    </Typography>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 'revert',
                                        alignContent: 'center',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                      }}
                                      key={`${opt.row}-col2-paper-box-sub`}
                                    >
                                      <Badge
                                        color={
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        badgeContent={
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                            ?.curTotalPoints
                                        }
                                        max={50001}
                                      >
                                        <Avatar
                                          alt={`${opt.row}_${opt.col}`}
                                          src={`/images/college/row${opt.row}_2.webp`}
                                          sx={{
                                            width: theme.spacing(7),
                                            height: theme.spacing(7)
                                          }}
                                        />
                                      </Badge>

                                      <Typography
                                        color={
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                            ? 'error'
                                            : 'primary'
                                        }
                                        variant='body2'
                                        sx={{
                                          marginTop: '-12px'
                                        }}
                                      >
                                        {collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter} /{' '}
                                        {collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter}{' '}
                                      </Typography>

                                      {/* MAXED */}
                                      {collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                        collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                          ?.maxCounter && (
                                        <Typography
                                          color={
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                              ?.counter ===
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                              ? 'error'
                                              : 'primary'
                                          }
                                          variant='body2'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          {collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter ===
                                          collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                            ? '(MAXED)'
                                            : ''}
                                        </Typography>
                                      )}

                                      {/* NEXT LEVEL */}
                                      {collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.counter !==
                                        collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                          ?.maxCounter && (
                                        <Typography
                                          color='textPrimary'
                                          variant='caption'
                                          style={{ marginTop: '1px', marginBottom: '-5px' }}
                                        >
                                          NEXT LVL: +
                                          {
                                            collegeInfo.options
                                              ?.find(o => o.row === opt.row && o.col === 2)
                                              .levels?.find(
                                                l =>
                                                  l.level ===
                                                  collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                                    .counter +
                                                    1
                                              )?.points
                                          }
                                        </Typography>
                                      )}

                                      <ButtonGroup
                                        color='primary'
                                        size='small'
                                        sx={{
                                          marginTop: '10px'
                                        }}
                                      >
                                        <Button
                                          aria-label='Decrease one level'
                                          disabled={
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                              ?.counter ===
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.minCounter
                                              ? true
                                              : false
                                          }
                                          onClick={() => {
                                            handleDrecrease(opt.row, 2)
                                          }}
                                        >
                                          <RemoveSharp fontSize='small' />
                                        </Button>
                                        <Button
                                          aria-label='Increase one level'
                                          onClick={() => {
                                            handleIncrease(opt.row, 2)
                                          }}
                                          disabled={
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                              ?.counter ===
                                            collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)?.maxCounter
                                              ? true
                                              : false
                                          }
                                        >
                                          <AddSharp fontSize='small' />
                                        </Button>
                                        <Tooltip describeChild title={'Fill skill at max.'}>
                                          <Button
                                            aria-label='increase-all'
                                            onClick={() => {
                                              handleIncrease(opt.row, 2, true)
                                            }}
                                            disabled={
                                              collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                                ?.counter ===
                                              collegeInfo.options?.find(o => o.row === opt.row && o.col === 2)
                                                ?.maxCounter
                                                ? true
                                                : false
                                            }
                                          >
                                            <AddToPhotos />{' '}
                                          </Button>
                                        </Tooltip>
                                        {!hideText && (
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 2)
                                              }}
                                              sx={{ minHeight: '32px', minWidth: '48px' }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        )}
                                      </ButtonGroup>
                                      {hideText && (
                                        <ButtonGroup
                                          color='primary'
                                          size='small'
                                          sx={{
                                            marginTop: '5px',
                                            minHeight: '32px',
                                            minWidth: '48px'
                                          }}
                                        >
                                          <Tooltip describeChild title={'Add prerequisites for this skill.'}>
                                            <Button
                                              variant='contained'
                                              aria-label='add-dependencies'
                                              size='small'
                                              color='primary'
                                              disabled={false}
                                              onClick={e => {
                                                handlePrerequisiteFill(opt.row, 2)
                                              }}
                                              sx={{ minHeight: '32px', minWidth: '48px' }}
                                            >
                                              {<AccountTree fontSize='inherit' />}
                                            </Button>
                                          </Tooltip>
                                        </ButtonGroup>
                                      )}
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>
                            </Grid>

                            <Divider light />
                          </>
                        )
                      }
                    })}
                  </Box>
                  {totalPoints > 0 && (
                    <Divider light>{<Typography variant='caption'>SELECTED TALENTS</Typography>}</Divider>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', maxWidth: '450px' }}>
                    {[...collegeInfo.options].map(opt => {
                      if (opt.counter > 0) {
                        let statDescription = { text: opt.statText }
                        opt.levels[opt.counter - 1].stat.forEach((s, i) => {
                          const indexText = `$${i + 1}`
                          statDescription.text = statDescription.text?.replace(indexText, s)
                        })

                        return (
                          <List key={`${opt.row}_${opt.col}_${opt.beast}`}>
                            <ListItem>
                              <ListItemAvatar>
                                <Avatar
                                  alt={`${opt.row}_${opt.col}`}
                                  src={`/images/college/row${opt.row}_${opt.col}.webp`}
                                />
                              </ListItemAvatar>
                              <ListItemText
                                primaryTypographyProps={{ color: 'primary' }}
                                primary={`${opt.description} - Lv${opt.counter}`}
                                secondary={statDescription.text}
                              />
                            </ListItem>
                          </List>
                        )
                      }
                    })}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
          {/*
            La clase la lee HuecoBarraInferior para medir la barra y levantar el pie
            del sitio lo mismo que mide. Sin eso, MUI le da z-index 1100 a este
            AppBar contra el 10 del pie, y la barra lo tapaba entero: los enlaces a
            /privacy/ y /terms/ no se veian ni se podian pulsar en esta pagina.
          */}
          <AppBar
            className='barra-inferior-fija'
            position='fixed'
            color='primary'
            sx={{
              top: 'auto',
              bottom: 0
            }}
          >
            <LinearProgressWithLabel value={progress} />
            <Toolbar>
              <Box sx={{ flexGrow: 1 }} />

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignContent: 'center' }}>
                <Typography variant='caption' sx={{ mt: '3px', color: 'common.white' }}>
                  Available
                </Typography>
                <Typography variant='body1' align='right' sx={{ color: 'common.white' }}>
                  {`${
                    collegeInfo.userTalents > 0
                      ? (collegeInfo.userTalents - totalPoints).toLocaleString()
                      : (totalAvailablePoints - totalPoints).toLocaleString()
                  }`}
                </Typography>
              </div>

              <Divider
                orientation='vertical'
                flexItem
                sx={{
                  margin: theme.spacing(1)
                }}
              />

              <GreenButton
                variant='contained'
                size='small'
                sx={{
                  margin: theme.spacing(1)
                }}
                onClick={handleFillAll}
                disabled={
                  collegeInfo.userTalents > 0
                    ? collegeInfo.userTalents - totalPoints <= 0
                      ? true
                      : false
                    : totalAvailablePoints - totalPoints <= 0
                    ? true
                    : false
                }
              >
                FILL
              </GreenButton>
              <RedButton
                variant='contained'
                size='small'
                sx={{
                  margin: theme.spacing(1)
                }}
                onClick={handleClearAll}
              >
                CLEAR
              </RedButton>

              <Divider
                orientation='vertical'
                flexItem
                sx={{
                  margin: theme.spacing(1)
                }}
              />

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', alignContent: 'center' }}>
                <Typography variant='caption' sx={{ mt: '3px', color: 'common.white' }}>
                  Used / Total
                </Typography>
                <Typography variant='body2' sx={{ color: 'common.white' }}>
                  {totalPoints.toLocaleString()} / {totalAvailablePoints.toLocaleString()}
                </Typography>
                <Typography variant='body2' sx={{ color: 'common.white' }}>
                  ${Math.round((totalPoints / 80) * 5).toLocaleString()}
                </Typography>
              </div>
            </Toolbar>
          </AppBar>
          <HuecoBarraInferior />
        </Card>
      </>
    )
  }

  return (
    <>
      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <CustomHeader icon='college' title='HIGH COLLEGE SIMULATOR' />
            <Divider sx={{ marginY: '1rem' }} />
            <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1rem', color: 'primary.main' }}>
              High College Simulator
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
              The High College Simulator helps you plan research upgrades for your War and Order High College. Configure different research paths and instantly see the resource requirements for each branch to prioritize effectively.
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Optimize your research order to unlock the most powerful bonuses faster, and avoid wasting resources on paths that don't align with your current strategy.
            </Typography>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Grid container spacing={6}>
            <Grid size={{ xs: 12 }} sx={{ pt: theme => `${theme.spacing(4)} !important` }}>
              {tabContentList[activeTab]}
            </Grid>
          </Grid>

          {/*
            Fuera del contenido por pestaña: es lo que pone los 260 niveles en el
            HTML y no solo los de la pestaña activa.
          */}
          <Grid size={{ xs: 12 }} sx={{ pt: 6 }}>
            <TablaReferencia
              titulo='Every High College level: azurite cost, running total and what the level gives'
              columnas={COLUMNAS_COLLEGE}
              filas={FILAS_COLLEGE}
              idFila='id'
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

HighCollege.noLoadingGate = true

export default HighCollege
